import { useMemo, useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { nip19 } from 'nostr-tools';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { useSpace } from '@/hooks/useSpaces';
import { useSpacePages } from '@/hooks/usePages';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAuthor } from '@/hooks/useAuthor';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

import {
  getTagValue, parsePageTags, buildPageTree, flattenPageTree,
  type PageEntry, type PageTreeNode, pageSlugFromDTag,
} from '@/lib/stele';
import {
  ChevronLeft, ChevronRight, ChevronDown, Edit2, Menu, Search, X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { ReaderSearch } from '@/components/reader/ReaderSearch';

export function Reader() {
  const { npub, spaceSlug, '*': pagePath } = useParams<{ npub: string; spaceSlug: string; '*': string }>();
  const navigate = useNavigate();
  const { user } = useCurrentUser();

  const pubkey = useMemo(() => {
    if (!npub) return undefined;
    try {
      const decoded = nip19.decode(npub);
      if (decoded.type === 'npub') return decoded.data;
    } catch { /* ignore */ }
    return undefined;
  }, [npub]);

  const { data: spaceEvent, isLoading: spaceLoading } = useSpace(pubkey, spaceSlug);
  const { data: pageEvents, isLoading: pagesLoading } = useSpacePages(pubkey, spaceSlug);
  const author = useAuthor(pubkey);

  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());

  const pageEntries = useMemo(() => spaceEvent ? parsePageTags(spaceEvent.tags) : [], [spaceEvent]);
  const tree        = useMemo(() => buildPageTree(pageEntries), [pageEntries]);
  const flatPages   = useMemo(() => flattenPageTree(tree), [tree]);

  const currentPath   = pagePath ? '/' + pagePath : (flatPages[0]?.path ?? '/');
  const activeEntry   = pageEntries.find(e => e.path === currentPath) ?? flatPages[0];
  const activePageEvent = useMemo(() =>
    activeEntry && pageEvents
      ? pageEvents.find(e => getTagValue(e.tags, 'd') === activeEntry.dTag) ?? null
      : null,
    [activeEntry, pageEvents]
  );

  const pageTitles = useMemo(() => {
    const map = new Map<string, string>();
    if (pageEvents) {
      for (const ev of pageEvents) {
        const d = getTagValue(ev.tags, 'd');
        const t = getTagValue(ev.tags, 'title');
        if (d && t) map.set(d, t);
      }
    }
    return map;
  }, [pageEvents]);

  const currentIndex = flatPages.findIndex(e => e.dTag === activeEntry?.dTag);
  const prevPage = currentIndex > 0 ? flatPages[currentIndex - 1] : null;
  const nextPage = currentIndex < flatPages.length - 1 ? flatPages[currentIndex + 1] : null;

  // Auto-expand parents
  useEffect(() => {
    if (!currentPath) return;
    const segs = currentPath.replace(/^\//, '').split('/');
    const paths = new Set<string>();
    for (let i = 1; i < segs.length; i++) paths.add('/' + segs.slice(0, i).join('/'));
    setExpandedPaths(prev => new Set([...prev, ...paths]));
  }, [currentPath]);

  // Scrollspy
  const [activeHeading, setActiveHeading] = useState('');
  const contentRef = useRef<HTMLDivElement>(null);

  const headings = useMemo(() => {
    if (!activePageEvent?.content) return [];
    return activePageEvent.content.split('\n')
      .flatMap(line => {
        const m = line.match(/^(#{2,3})\s+(.+)/);
        if (!m) return [];
        const text = m[2].trim();
        const id = text.toLowerCase().replace(/[^\w]+/g, '-').replace(/^-|-$/g, '');
        return [{ id, text, level: m[1].length }];
      });
  }, [activePageEvent]);

  useEffect(() => {
    if (!headings.length) return;
    const obs = new IntersectionObserver(
      entries => { for (const e of entries) { if (e.isIntersecting) setActiveHeading(e.target.id); } },
      { rootMargin: '-80px 0px -80% 0px' }
    );
    headings.map(h => document.getElementById(h.id)).filter(Boolean).forEach(el => obs.observe(el!));
    return () => obs.disconnect();
  }, [headings, activePageEvent]);

  // ⌘K search
  useEffect(() => {
    const fn = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); setSearchOpen(true); }
    };
    document.addEventListener('keydown', fn);
    return () => document.removeEventListener('keydown', fn);
  }, []);

  const spaceTitle = spaceEvent ? getTagValue(spaceEvent.tags, 'title') ?? 'Untitled' : '';
  const spaceIcon  = spaceEvent ? getTagValue(spaceEvent.tags, 'icon') ?? '' : '';
  const pageTitle  = activePageEvent ? getTagValue(activePageEvent.tags, 'title') ?? 'Untitled' : '';
  const isOwner    = user?.pubkey === pubkey;

  useSeoMeta({
    title: pageTitle ? `${pageTitle} — ${spaceTitle}` : spaceTitle || 'Stele',
    description: activePageEvent ? getTagValue(activePageEvent.tags, 'summary') ?? '' : '',
  });

  const toggleExpand = (path: string) =>
    setExpandedPaths(prev => { const s = new Set(prev); s.has(path) ? s.delete(path) : s.add(path); return s; });

  const navigateToPage = useCallback((path: string) => {
    navigate(`/r/${npub}/${spaceSlug}${path}`);
    setMobileSidebarOpen(false);
  }, [navigate, npub, spaceSlug]);

  const renderTreeNode = (node: PageTreeNode, depth = 0) => {
    const isActive    = node.entry.dTag === activeEntry?.dTag;
    const hasChildren = node.children.length > 0;
    const isExpanded  = expandedPaths.has(node.entry.path);
    const title = pageTitles.get(node.entry.dTag) ?? pageSlugFromDTag(node.entry.dTag) ?? 'Untitled';

    return (
      <div key={node.entry.dTag}>
        <button
          className={cn(
            'flex w-full items-center gap-1 py-[5px] pr-2 text-[13px] text-left transition-colors',
            isActive
              ? 'text-foreground bg-accent'
              : 'text-muted-foreground hover:text-foreground hover:bg-accent/40',
          )}
          style={{ paddingLeft: `${depth * 14 + 10}px` }}
          onClick={() => navigateToPage(node.entry.path)}
        >
          {hasChildren ? (
            <button onClick={(e) => { e.stopPropagation(); toggleExpand(node.entry.path); }} className="shrink-0">
              <ChevronDown className={cn('h-3 w-3 transition-transform', !isExpanded && '-rotate-90')} />
            </button>
          ) : (
            <span className="w-3 shrink-0" />
          )}
          <span className="truncate leading-snug">{title}</span>
        </button>
        {hasChildren && isExpanded && node.children.map(c => renderTreeNode(c, depth + 1))}
      </div>
    );
  };

  /* ── Loading ── */
  if (spaceLoading || pagesLoading) {
    return (
      <div className="min-h-screen bg-background">
        <header className="h-11 border-b border-border flex items-center px-6">
          <Skeleton className="h-4 w-32" />
        </header>
        <div className="flex">
          <aside className="w-56 border-r border-border p-4 hidden lg:block space-y-2">
            {[80, 60, 90, 50].map((w, i) => <Skeleton key={i} className="h-4" style={{ width: `${w}%` }} />)}
          </aside>
          <main className="flex-1 p-10 space-y-4 max-w-2xl">
            <Skeleton className="h-9 w-64" />
            {[100, 100, 80].map((_, i) => <Skeleton key={i} className="h-4 w-full" />)}
          </main>
        </div>
      </div>
    );
  }

  if (!spaceEvent) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-xs font-mono tracking-[0.2em] text-muted-foreground uppercase mb-4">404</p>
          <h1 className="text-2xl font-bold mb-2">Space not found</h1>
          <p className="text-muted-foreground text-sm mb-6">This documentation space doesn't exist.</p>
          <Button variant="outline" size="sm" onClick={() => navigate('/')}>← Home</Button>
        </div>
      </div>
    );
  }

  const lastUpdated = activePageEvent ? new Date(activePageEvent.created_at * 1000) : null;

  return (
    <div className="min-h-screen bg-background">

      {/* ── Header ── */}
      <header className="sticky top-0 z-40 h-11 border-b border-border bg-background/90 backdrop-blur">
        <div className="flex items-center justify-between h-full px-4 lg:px-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-7 w-7 lg:hidden text-muted-foreground"
              onClick={() => setMobileSidebarOpen(true)}>
              <Menu className="h-3.5 w-3.5" />
            </Button>
            <Link to={`/r/${npub}/${spaceSlug}`} className="flex items-center gap-2 hover:opacity-70 transition-opacity">
              {spaceIcon && <span className="text-base">{spaceIcon}</span>}
              <span className="font-mono text-xs tracking-widest uppercase text-foreground">{spaceTitle}</span>
            </Link>
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={() => setSearchOpen(true)}
              className="hidden sm:flex items-center gap-2 h-7 px-3 text-xs text-muted-foreground hover:text-foreground transition-colors font-mono border border-border rounded-sm"
            >
              <Search className="h-3 w-3" />
              <span>Search</span>
              <kbd className="ml-1 text-[10px] opacity-50">⌘K</kbd>
            </button>
            <Button variant="ghost" size="icon" className="h-7 w-7 sm:hidden text-muted-foreground"
              onClick={() => setSearchOpen(true)}>
              <Search className="h-3.5 w-3.5" />
            </Button>
            {isOwner && spaceSlug && (
              <Button variant="ghost" size="sm" className="h-7 gap-1.5 text-xs font-mono px-3 text-muted-foreground hover:text-foreground"
                onClick={() => navigate(`/s/${spaceSlug}`)}>
                <Edit2 className="h-3 w-3" />
                <span className="hidden sm:inline">Edit</span>
              </Button>
            )}
            <ThemeToggle />
          </div>
        </div>
      </header>

      <div className="flex">

        {/* ── Desktop sidebar ── */}
        <aside className="hidden lg:block w-56 shrink-0 border-r border-border sticky top-11 h-[calc(100vh-2.75rem)] overflow-y-auto py-3">
          {tree.map(node => renderTreeNode(node))}
        </aside>

        {/* ── Mobile sidebar ── */}
        {mobileSidebarOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div className="absolute inset-0 bg-black/70" onClick={() => setMobileSidebarOpen(false)} />
            <aside className="absolute left-0 top-0 bottom-0 w-64 bg-background border-r border-border overflow-y-auto">
              <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                <span className="font-mono text-xs tracking-widest uppercase">{spaceTitle}</span>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground"
                  onClick={() => setMobileSidebarOpen(false)}>
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
              <div className="py-3">{tree.map(node => renderTreeNode(node))}</div>
            </aside>
          </div>
        )}

        {/* ── Main content ── */}
        <main className="flex-1 min-w-0">
          <div className="max-w-[720px] mx-auto px-6 lg:px-10 py-12">
            {activePageEvent ? (
              <article ref={contentRef}>
                <h1 className="text-[2rem] font-bold tracking-[-0.04em] leading-tight mb-10">{pageTitle}</h1>

                {/* Prose */}
                <div className={cn(
                  'reader-prose',
                  'text-[0.9375rem] leading-[1.85] text-foreground/90',
                )}>
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      h2: ({ children, ...props }) => {
                        const id = String(children).toLowerCase().replace(/[^\w]+/g, '-').replace(/^-|-$/g, '');
                        return <h2 id={id} className="text-[1.375rem] font-semibold tracking-[-0.025em] mt-10 mb-4 scroll-mt-20 text-foreground" {...props}>{children}</h2>;
                      },
                      h3: ({ children, ...props }) => {
                        const id = String(children).toLowerCase().replace(/[^\w]+/g, '-').replace(/^-|-$/g, '');
                        return <h3 id={id} className="text-base font-semibold tracking-[-0.01em] mt-7 mb-3 scroll-mt-20 text-foreground" {...props}>{children}</h3>;
                      },
                      p: ({ children }) => <p className="mb-4">{children}</p>,
                      ul: ({ children }) => <ul className="list-disc pl-5 mb-4 space-y-1">{children}</ul>,
                      ol: ({ children }) => <ol className="list-decimal pl-5 mb-4 space-y-1">{children}</ol>,
                      li: ({ children }) => <li className="leading-relaxed">{children}</li>,
                      a: ({ href, children }) => (
                        <a href={href} className="text-foreground underline decoration-muted-foreground underline-offset-3 hover:decoration-foreground transition-colors" target={href?.startsWith('http') ? '_blank' : undefined} rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}>
                          {children}
                        </a>
                      ),
                      blockquote: ({ children, ...props }) => {
                        // Callout detection
                        const firstChild = Array.isArray(children) ? children.find((c: unknown) => typeof c === 'object' && c !== null) : children;
                        const textContent = (firstChild as { props?: { children: unknown } })?.props?.children;
                        const text = Array.isArray(textContent) ? textContent.join('') : String(textContent ?? '');
                        const calloutMatch = text.match(/^\[!(\w+)\]\s*(.*)/);
                        if (calloutMatch) {
                          const type = calloutMatch[1].toLowerCase();
                          const borderMap: Record<string, string> = {
                            info:    'border-blue-500/60',
                            warning: 'border-yellow-500/60',
                            success: 'border-green-500/60',
                            danger:  'border-red-500/60',
                            note:    'border-blue-500/60',
                            tip:     'border-green-500/60',
                          };
                          const bgMap: Record<string, string> = {
                            info:    'bg-blue-500/5',
                            warning: 'bg-yellow-500/5',
                            success: 'bg-green-500/5',
                            danger:  'bg-red-500/5',
                            note:    'bg-blue-500/5',
                            tip:     'bg-green-500/5',
                          };
                          return (
                            <div className={cn('border-l-2 px-4 py-3 my-5 rounded-r-sm', borderMap[type] ?? 'border-blue-500/60', bgMap[type] ?? 'bg-blue-500/5')}>
                              <div className="text-[10px] font-mono tracking-[0.18em] uppercase text-muted-foreground mb-1.5">{type}</div>
                              <div className="text-sm text-foreground/80">{calloutMatch[2]}</div>
                            </div>
                          );
                        }
                        return (
                          <blockquote className="border-l-2 border-border pl-4 text-muted-foreground italic my-4" {...props}>
                            {children}
                          </blockquote>
                        );
                      },
                      code: ({ className, children, ...props }) => {
                        const isBlock = className?.includes('language-');
                        if (isBlock) return <code className={cn('text-[0.8rem] font-mono', className)} {...props}>{children}</code>;
                        return <code className="bg-muted border border-border px-1.5 py-0.5 rounded-sm font-mono text-[0.8rem] text-foreground/90" {...props}>{children}</code>;
                      },
                      pre: ({ children }) => (
                        <div className="relative group my-5">
                          <pre className="bg-card border border-border rounded-sm p-5 overflow-x-auto font-mono text-[0.8125rem] leading-relaxed">
                            {children}
                          </pre>
                          <button
                            className="absolute top-2.5 right-2.5 opacity-0 group-hover:opacity-100 transition-opacity text-[10px] font-mono tracking-wider uppercase text-muted-foreground border border-border rounded-sm px-2 py-1 hover:text-foreground hover:border-foreground/30 bg-card"
                            onClick={(e) => {
                              const code = (e.currentTarget.parentElement?.querySelector('code') as HTMLElement)?.textContent;
                              if (code) navigator.clipboard.writeText(code);
                            }}
                          >
                            Copy
                          </button>
                        </div>
                      ),
                      hr: () => <hr className="border-t border-border my-8" />,
                      img: ({ src, alt }) => (
                        <img src={src} alt={alt} className="rounded-sm border border-border my-4 max-w-full" />
                      ),
                    }}
                  >
                    {activePageEvent.content}
                  </ReactMarkdown>
                </div>

                {/* ── Footer ── */}
                <footer className="mt-16 pt-8 border-t border-border">
                  {lastUpdated && (
                    <p className="text-[11px] font-mono text-muted-foreground/50 mb-8">
                      Updated {lastUpdated.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                      {pubkey && (
                        <> · <Link to={`/${npub}`} className="hover:text-muted-foreground transition-colors">{author.data?.metadata?.name ?? npub?.slice(0, 16) + '…'}</Link></>
                      )}
                    </p>
                  )}

                  {/* Prev / Next */}
                  <div className="flex justify-between gap-4">
                    {prevPage ? (
                      <button onClick={() => navigateToPage(prevPage.path)}
                        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors group">
                        <ChevronLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
                        <div className="text-left">
                          <div className="text-[10px] font-mono tracking-widest uppercase text-muted-foreground/50 mb-0.5">Prev</div>
                          <div className="font-medium text-sm">{pageTitles.get(prevPage.dTag) ?? pageSlugFromDTag(prevPage.dTag)}</div>
                        </div>
                      </button>
                    ) : <div />}
                    {nextPage ? (
                      <button onClick={() => navigateToPage(nextPage.path)}
                        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors text-right group">
                        <div>
                          <div className="text-[10px] font-mono tracking-widest uppercase text-muted-foreground/50 mb-0.5">Next</div>
                          <div className="font-medium text-sm">{pageTitles.get(nextPage.dTag) ?? pageSlugFromDTag(nextPage.dTag)}</div>
                        </div>
                        <ChevronRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                      </button>
                    ) : <div />}
                  </div>

                  {/* Byline */}
                  <div className="mt-12 flex items-center justify-center gap-3 text-[10px] font-mono tracking-[0.15em] uppercase text-muted-foreground/30">
                    <span>Powered by Nostr</span>
                    <span>·</span>
                    <a href="https://shakespeare.diy" target="_blank" rel="noopener noreferrer"
                      className="hover:text-muted-foreground transition-colors">
                      Vibed with Shakespeare
                    </a>
                  </div>
                </footer>
              </article>
            ) : (
              <div className="flex items-center justify-center py-24">
                <p className="text-xs font-mono tracking-[0.2em] text-muted-foreground/30 uppercase">Page not found</p>
              </div>
            )}
          </div>
        </main>

        {/* ── TOC ── */}
        {headings.length > 0 && (
          <aside className="hidden xl:block w-52 shrink-0 sticky top-11 h-[calc(100vh-2.75rem)] overflow-y-auto py-6 px-4">
            <p className="text-[10px] font-mono tracking-[0.18em] uppercase text-muted-foreground/40 mb-4">
              On this page
            </p>
            <nav className="space-y-0.5">
              {headings.map(h => (
                <a
                  key={h.id}
                  href={`#${h.id}`}
                  className={cn(
                    'block text-[12px] py-1 transition-colors leading-snug',
                    h.level === 3 && 'pl-3',
                    activeHeading === h.id
                      ? 'text-foreground'
                      : 'text-muted-foreground/50 hover:text-muted-foreground',
                  )}
                >
                  {h.text}
                </a>
              ))}
            </nav>
          </aside>
        )}
      </div>

      {searchOpen && pageEvents && (
        <ReaderSearch
          pages={pageEvents}
          pageEntries={pageEntries}
          pageTitles={pageTitles}
          onClose={() => setSearchOpen(false)}
          onNavigate={(path) => { navigateToPage(path); setSearchOpen(false); }}
        />
      )}
    </div>
  );
}
